import { use, useContext, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ContractDetailModel,
  EnvelopeSubmissionStatus,
  EnvelopeSubmissionType,
} from '@urbanx/agx-ui-components'
import { NotificationContext } from 'contexts'
import { GetContractById } from 'services'
import { CampaignUpdatedEvent, NotificationType } from 'types/pushNotifications'
import { AuthContext } from '@urbanx/agx.auth.client'

const useSelectedContract = (): {
  contract?: ContractDetailModel
  isDocuSigned: boolean
  isLoading: boolean
  isFetched: boolean
  campaignId?: string
} => {
  const { campaignId, contractId } = useParams()
  const queryClient = useQueryClient()
  const { getAuthToken } = useContext(AuthContext)
  const notifications = use(NotificationContext)
  const contractQueryKey = ['contract-details', contractId]

  const campaignUpdated = async (event: CampaignUpdatedEvent) => {
    if (event.campaignId === campaignId) {
      await queryClient.invalidateQueries({ queryKey: contractQueryKey })
    }
  }

  useEffect(() => {
    if (!contractId) return

    notifications?.registerEvent(
      NotificationType.CampaignUpdatedEvent,
      campaignUpdated,
      contractQueryKey
    )
  }, [contractQueryKey])

  const {
    data: contract,
    isLoading,
    isFetched,
  } = useQuery<ContractDetailModel | undefined>({
    queryKey: contractQueryKey,
    queryFn: (ctx) => GetContractById(getAuthToken, ctx.queryKey[1] as string),
    enabled: !!contractId,
  })

  const isDocuSigned = useMemo(() => {
    return (
      contract?.envelopeSubmission?.type === EnvelopeSubmissionType.DocuSign &&
      (contract?.envelopeSubmission?.status ===
        EnvelopeSubmissionStatus.Signed ||
        contract?.envelopeSubmission?.status ===
          EnvelopeSubmissionStatus.Complete)
    )
  }, [contract])

  return {
    contract,
    isDocuSigned,
    isLoading,
    isFetched,
    campaignId,
  }
}

export default useSelectedContract
